import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, optionalAvathar, defaultAllSelection } from "services/constants";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Groups2Rounded from "@mui/icons-material/Groups2Rounded";
import LoginRounded from "@mui/icons-material/LoginRounded";
import AssignmentLateRounded from "@mui/icons-material/AssignmentLateRounded";
import HourglassBottomRounded from "@mui/icons-material/HourglassBottomRounded";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import {
  checkViewerPermission,
  findSubModuleByName,
} from "services/utils/HelperFunction";
import { dailyReportHeader } from "../TeamZoneHeaders";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

const timeToMinutes = (value) => {
  const match = /^(\d{1,4}):(\d{2})$/.exec(`${value ?? ""}`.trim());
  if (!match) return 0;
  return Number(match[1]) * 60 + Number(match[2]);
};

const DAILY_REPORT_EMPLOYEE_NAME_WIDTH = "11%";

const sumHhMm = (values) => {
  const totalMinutes = values.reduce((sum, value) => sum + timeToMinutes(value), 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

export const DailyReport = ({
  setShowReport,
  setReportHeaderActions,
  embeddedReportTypes = null,
  selectedReportType = null,
  onReportTypeChange = null,
}) => {
  const {
    domain,
    token,
    userDetails,
    topbarCompanyId,
    userScreenAccess,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dailyRepDetails, setDailyRepDetails] = useState([]);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );
  const [departmentName, setDepartmentName] = useState("All");
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [dailyReportData, setDailyReportData] = useState([]);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const { downloadExcel, downloadPdf, downloadCsv } = useExcelDownload();

  const subModuleId = findSubModuleByName(
    userScreenAccess?.module_details,
    "Reports",
    "Daily Report",
  )?.sub_module_id;

  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Daily Report",
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "daily_report_saved_views",
    fullHeaders: dailyReportHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  useEffect(() => {
    const fetchUsertaskDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${domain}checkinfilter/?department_id=${departmentId}&date=${startDate}&user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          const rows = res?.user_status_details || [];
          setDailyRepDetails(rows);
          setDailyReportData(rows);
        } else if (response.status === 409) {
          setDailyRepDetails([]);
          setDailyReportData([]);
          sessionHandling();
        } else {
          setDailyRepDetails([]);
          setDailyReportData([]);
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Dailyreport] operation failed:", error);
        setDailyRepDetails([]);
        setDailyReportData([]);
        showToast(
          "error",
          "Couldn't load the attendance report. Please try again.",
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsertaskDetails();
  }, [domain, token, startDate, departmentId, userDetails.id, topbarCompanyId]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStartDate = (rangeStart) => {
    const parsedStartDate = moment(rangeStart);
    if (!parsedStartDate.isValid()) return;
    setStartDate(parsedStartDate.format("YYYY-MM-DD"));
    setPage(0);
  };

  const departmentList = useMemo(
    () => [
      {
        id: defaultAllSelection,
        label: "All",
        department_name: "All",
      },
      ...(departmentData?.map((obj) => ({
        ...obj,
        label: obj.department_name,
      })) ?? []),
    ],
    [departmentData],
  );

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setDailyReportData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const updateDepartmentName = (event, data) => {
    if (data && data?.label) {
      setDepartmentName(data.label);
      setDepartmentId(data.id);
      setPage(0);
    }
  };

  const getReportMetadata = useCallback(() => {
    const formattedStartDate = moment(
      startDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");
    const rows = dailyReportData || [];

    return {
      "Company Name": topbarCompanyName || "N/A",
      Department: departmentName || "All",
      "Date Range": `${formattedStartDate}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      Employees: rows.length,
      "Checked In": rows.filter((row) => row?.check_in_time_formatted).length,
      "Late Coming": rows.filter((row) => timeToMinutes(row?.late_coming_minutes) > 0)
        .length,
      "Early Leaving": rows.filter(
        (row) => timeToMinutes(row?.early_leaving_minutes) > 0,
      ).length,
      "Total Work Hours": sumHhMm(rows.map((row) => row?.total_work_time)),
    };
  }, [departmentName, dailyReportData, startDate, topbarCompanyName]);

  const handleDailyReportDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const summaryRow = {
        rowType: "grand_total",
        user_name: "TOTAL",
        total_break_time: sumHhMm(rows.map((row) => row?.total_break_time)),
        total_work_time: sumHhMm(rows.map((row) => row?.total_work_time)),
        total_over_time: sumHhMm(rows.map((row) => row?.total_over_time)),
        late_coming_minutes: sumHhMm(rows.map((row) => row?.late_coming_minutes)),
        early_leaving_minutes: sumHhMm(
          rows.map((row) => row?.early_leaving_minutes),
        ),
      };

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        visibleHeaders,
        "Daily Report",
        {},
        getReportMetadata(),
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [downloadCsv, downloadExcel, downloadPdf, getReportMetadata, visibleHeaders],
  );

  // Same StatCard row every other report in this migration now shows,
  // derived from the currently visible (searched/filtered) rows.
  const dailySummaryCards = useMemo(() => {
    const rows = dailyReportData || [];
    return [
      {
        key: "employees",
        title: "Employees",
        value: rows.length,
        icon: Groups2Rounded,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
      {
        key: "checkedIn",
        title: "Checked In",
        value: rows.filter((row) => row?.check_in_time_formatted).length,
        icon: LoginRounded,
        accent: "#0F766E",
        tint: "#E6F8F4",
      },
      {
        key: "lateComing",
        title: "Late Coming",
        value: rows.filter((row) => timeToMinutes(row?.late_coming_minutes) > 0)
          .length,
        icon: AssignmentLateRounded,
        accent: "#EF4444",
        tint: "#FEF2F2",
      },
      {
        key: "earlyLeaving",
        title: "Early Leaving",
        value: rows.filter(
          (row) => timeToMinutes(row?.early_leaving_minutes) > 0,
        ).length,
        icon: HourglassBottomRounded,
        accent: "#7C3AED",
        tint: "#F5F3FF",
      },
      {
        key: "workHours",
        title: "Total Work Hours",
        value: sumHhMm(rows.map((row) => row?.total_work_time)),
        icon: ScheduleRounded,
        accent: "#F97316",
        tint: "#FFF7ED",
      },
    ];
  }, [dailyReportData]);

  const selectedReportTypeOption =
    embeddedReportTypes?.find(
      (option) =>
        option?.label === selectedReportType ||
        option?.value === selectedReportType,
    ) || null;

  const headerFilterControls = useMemo(
    () => (
      <Mui.Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          startDate={startDate}
          endDate={startDate}
          onChange={handleStartDate}
          showToday={true}
          includeCurrentDate={true}
          singleDatePicker={true}
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={departmentList}
          selectedValue={departmentName}
          updateDataFunction={updateDepartmentName}
          Icons={PersonOutlineIcon}
          staticLabel="Department"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 220 },
          }}
        />
        {embeddedReportTypes?.length ? (
          <CompactHeaderSelectFilter
            dataList={embeddedReportTypes}
            selectedValue={selectedReportTypeOption}
            updateDataFunction={onReportTypeChange}
            Icons={PersonOutlineIcon}
            staticLabel="Report Type"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 260 },
            }}
          />
        ) : null}
      </Mui.Stack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      departmentList,
      departmentName,
      embeddedReportTypes,
      selectedReportTypeOption,
      onReportTypeChange,
    ],
  );

  // Filters render inline in the page body (matching Employee Attendance's
  // own row below the title), not floated beside the title.
  useEffect(() => {
    if (!setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [setReportHeaderActions]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const renderDailyReportCell = (key, item) => {
    const renderTruncatedValue = (value, fallback = "N/A") => (
      <TruncatedTableValue value={value || fallback} />
    );

    switch (key) {
      case "user_name":
        return (
          <TruncatedTableValue value={item?.user_name || "Unassigned"}>
            <Mui.Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0, width: "100%" }}
            >
              <Mui.Avatar
                sx={{ width: 28, height: 28 }}
                alt="Remy Sharp"
                src={item?.image_url ?? optionalAvathar}
              />
              <Mui.Typography
                noWrap
                data-table-value-content
                sx={{
                  minWidth: 0,
                  color: designColors.slate900,
                  fontSize: 14,
                }}
              >
                {item?.user_name || "Unassigned"}
              </Mui.Typography>
            </Mui.Stack>
          </TruncatedTableValue>
        );
      case "employee_id":
        return (
          <TruncatedTableValue
            value={item?.employee_id || "Unassigned"}
          >
            {item?.employee_id || "Unassigned"}
          </TruncatedTableValue>
        );
      case "department_name":
        return (
          <TruncatedTableValue
            value={item?.department_name || "Unassigned"}
          >
            {item?.department_name || "Unassigned"}
          </TruncatedTableValue>
        );
      case "shift_name":
        return (
          <TruncatedTableValue value={item?.shift_name || "Unassigned"}>
            {item?.shift_name || "Unassigned"}
          </TruncatedTableValue>
        );
      case "check_in_time_formatted":
        return renderTruncatedValue(item?.check_in_time_formatted);
      case "check_out_time_formatted":
        return renderTruncatedValue(item?.check_out_time_formatted);
      case "total_break_time":
        return renderTruncatedValue(item?.total_break_time, "00:00");
      case "total_work_time":
        return renderTruncatedValue(item?.total_work_time, "00:00");
      case "total_over_time":
        return renderTruncatedValue(item?.total_over_time, "00:00");
      case "late_coming_minutes":
        return renderTruncatedValue(item?.late_coming_minutes, "00:00");
      case "early_leaving_minutes":
        return renderTruncatedValue(item?.early_leaving_minutes, "00:00");
      case "location_name":
        return renderTruncatedValue(item?.location_name);
      case "type":
        return renderTruncatedValue(item?.type);
      case "modified_by":
        return renderTruncatedValue(item?.modified_by, "-");
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          {/* Filters sit in their own row directly under the page title —
              same placement as Employee Attendance Report — instead of
              floating beside the title via setReportHeaderActions. */}
          <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
            {headerFilterControls}
          </Mui.Box>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(5, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {dailySummaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Daily"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={dailyRepDetails}
              filteredData={dailyReportData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              hideCount={true}
              isAttendanceScreenReport={true}
              showDownloadOptions={false}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={getReportMetadata()}
              customDownloadHandler={handleDailyReportDownload}
              labeledToolbar={true}
              searchPlaceholder="Search by employee, department..."
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            {isLoading ? (
              <Loader info="Loading..." />
            ) : (
              <Mui.Box>
                <Mui.TableContainer
                  sx={{
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: { xs: "auto", sm: "visible" },
                    overflowY: { xs: "auto", sm: "visible" },
                    pb: footerHeight ? `${footerHeight}px` : 0,
                    borderLeft: `1px solid ${designColors.slate200}`,
                    borderRight: `1px solid ${designColors.slate200}`,
                    borderBottom: `1px solid ${designColors.slate200}`,
                  }}
                >
                  <ApproveTable
                    stickyHeader
                    sx={{ minWidth: 960, width: "100%", tableLayout: "fixed" }}
                  >
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                        {visibleHeaders.map((header, headerIndex) => (
                          <Mui.TableCell
                            key={header.key}
                            sx={{
                              ...approveTableHeadCell,
                              top: APP_BAR_OFFSET + customFilterHeight,
                              ...(headerIndex === 0
                                ? { borderTopLeftRadius: 0 }
                                : {}),
                              ...(headerIndex === visibleHeaders.length - 1
                                ? { borderTopRightRadius: 0 }
                                : {}),
                              ...(header.key === "user_name"
                                ? { width: DAILY_REPORT_EMPLOYEE_NAME_WIDTH }
                                : {}),
                            }}
                          >
                            <TruncatedTableValue value={header.name} />
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {dailyReportData?.length === 0 ? (
                        <TableEmptyState dataLength={dailyRepDetails} />
                      ) : (
                        dailyReportData
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((item) => (
                            <Mui.TableRow
                              key={item?.id}
                              hover
                              sx={{
                                ...designShell.tableRowHover,
                                "&:last-child td": { borderBottom: "none" },
                                "& td": { backgroundColor: designColors.white },
                              }}
                            >
                              {visibleHeaders.map((header) => (
                                <Mui.TableCell
                                  key={header.key}
                                  sx={{
                                    ...approveTableBodyCell,
                                    ...(header.key === "user_name"
                                      ? {
                                          width:
                                            DAILY_REPORT_EMPLOYEE_NAME_WIDTH,
                                        }
                                      : {}),
                                  }}
                                >
                                  {renderDailyReportCell(header.key, item)}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                  </ApproveTable>
                </Mui.TableContainer>
              </Mui.Box>
            )}

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: dailyReportData?.length || 0,
                itemLabel: "records",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
